// src/pages/Pos.js — sensorli POS monoblok uchun oddiy (chakana) savdo ekrani
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Pos.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { useSklad } from "../SkladContext";
import { adminPath } from "../routes";
import { getReceiptPrintExtras, renderReceiptHTML } from "../receiptPrinting";

/* ================== Config ================== */
const PAGE_SIZE = 48;
const SEARCH_DEBOUNCE_MS = 250;
const LS_CUSTOMER = "pos_default_customer";
const LS_PRINT = "pos_auto_print";
const QUICK_CASH = [1000, 5000, 10000, 20000, 50000, 100000, 200000];

/* ================== Helpers ================== */
const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (v) => Math.round(toNum(v)).toLocaleString("uz-UZ");
const money3 = (v) => toNum(v).toFixed(3);
const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const stockOf = (p) => Number(p?.soni ?? 0);
const uniqById = (list) => {
  const seen = new Set();
  return list.filter((it) => (seen.has(it.id) ? false : seen.add(it.id)));
};
const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

// Mahsulotdagi narxni narx turi nomi bo'yicha topish (narxlar[].sarxturi)
const priceFor = (p, label) => {
  const list = Array.isArray(p?.narxlar) ? p.narxlar : [];
  const lab = String(label || "").toLowerCase();
  const hit = list.find((n) => String(n.sarxturi ?? n.narxturi ?? "").toLowerCase() === lab);
  if (hit) return toNum(hit.narx);
  const donaga = list.find((n) => String(n.sarxturi ?? "").toLowerCase() === "donaga");
  return toNum((donaga || list[0])?.narx);
};

// Chekni yashirin iframe orqali bosish: Chrome --kiosk-printing bilan dialogsiz chiqadi
const printReceipt = (receipt, extras) => {
  const html = renderReceiptHTML(receipt, extras);
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:-10000px;bottom:-10000px;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {}
    setTimeout(() => iframe.remove(), 60000);
  }, 300);
};

/* ================== Numpad ================== */
function Numpad({ value, onChange, allowDot = false }) {
  const press = (k) => {
    if (k === "C") return onChange("");
    if (k === "⌫") return onChange(String(value || "").slice(0, -1));
    if (k === "." && (!allowDot || String(value).includes("."))) return;
    if (k === "00" && !value) return;
    const next = `${value || ""}${k}`.replace(/^0+(?=\d)/, "");
    if (next.length > 12) return;
    onChange(next);
  };
  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3", allowDot ? "." : "00", "0", "⌫"];
  return (
    <div className="pos-numpad">
      {keys.map((k) => (
        <button key={k} type="button" className={k === "⌫" ? "np-key np-back" : "np-key"} onClick={() => press(k)}>
          {k}
        </button>
      ))}
      <button type="button" className="np-key np-clear" onClick={() => press("C")}>Tozalash</button>
    </div>
  );
}

/* ================== Product card ================== */
const PosCard = React.memo(function PosCard({ product, price, inCartQty, onAdd }) {
  const stock = stockOf(product);
  const out = stock <= 0 || product.can_sell === false;
  return (
    <button
      type="button"
      className={`pos-card${out ? " out" : ""}${inCartQty ? " in-cart" : ""}`}
      onClick={() => !out && onAdd(product)}
      disabled={out}
    >
      {inCartQty > 0 && <span className="pos-card-badge">{inCartQty}</span>}
      <span className="pos-card-name">{product.nom}</span>
      <span className="pos-card-sku">{product.seriyasi}</span>
      <span className="pos-card-foot">
        <b className="pos-card-price">{product.can_view_prices === false ? "—" : fmt(price)}</b>
        <span className="pos-card-stock">{out ? "Yo‘q" : `${stock} dona`}</span>
      </span>
    </button>
  );
});

/* ================== Asosiy sahifa ================== */
export default function PosPage() {
  const navigate = useNavigate();
  const access = useMemo(() => getWarehouseAccess(), []);
  const { activeSklad } = useSklad();
  const warehouse = !access.allAccess && access.allowedIds.length === 1
    ? String(access.allowedIds[0])
    : activeSklad || "all";

  const searchRef = useRef(null);
  const loaderRef = useRef(null);

  /* Ma'lumotnomalar */
  const [categories, setCategories] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [priceType, setPriceType] = useState("donaga");

  /* Mahsulotlar */
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [category, setCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  /* Xaridor */
  const [customer, setCustomer] = useState(null);
  const [customerPicker, setCustomerPicker] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerQ, setCustomerQ] = useState("");

  /* Savat */
  const [cart, setCart] = useState([]);
  const [lastAddedId, setLastAddedId] = useState(null);

  /* Modallar */
  const [editLine, setEditLine] = useState(null); // {id, field: "qty"|"price", value}
  const [payOpen, setPayOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("naqd"); // naqd | karta | terminal | aralash
  const [cashIn, setCashIn] = useState("");
  const [cardIn, setCardIn] = useState("");
  const [terminalIn, setTerminalIn] = useState("");
  const [mixField, setMixField] = useState("cash");
  const [discount, setDiscount] = useState("");
  const [discountOpen, setDiscountOpen] = useState(false);
  const [scanChoices, setScanChoices] = useState(null); // { code, items } — bir xil kodli mahsulotlar
  const [autoPrint, setAutoPrint] = useState(() => lsGet(LS_PRINT) !== "0");
  const [submitting, setSubmitting] = useState(false);

  /* Toast */
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((text, kind = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "err" ? 5000 : 2500);
  }, []);

  const focusSearch = useCallback(() => {
    setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 0);
  }, []);

  /* ------- Init ------- */
  useEffect(() => {
    (async () => {
      try {
        const [t, c, s] = await Promise.all([
          api.get("narx-turi/"),
          api.get("maxsulot-kategoriyasi/"),
          api.get("sotuvchi/"),
        ]);
        setPriceTypes(asArr(t));
        setCategories(asArr(c));
        const list = asArr(s);
        setCustomers(list);
        const savedId = lsGet(LS_CUSTOMER);
        const saved = list.find((x) => String(x.id) === String(savedId));
        if (saved) setCustomer(saved);
        else setCustomerPicker(true);
      } catch (e) {
        toast("Ma'lumotlarni yuklashda xato", "err");
      }
    })();
  }, [toast]);

  // Xaridor tarifi narx turini belgilaydi (masalan "Vip klient"), aks holda Donaga
  useEffect(() => {
    const tariff = String(customer?.mijoztarifi || "").toLowerCase();
    const exists = priceTypes.some((pt) => String(pt.nom || "").toLowerCase() === tariff);
    setPriceType(exists ? tariff : "donaga");
  }, [customer, priceTypes]);

  /* ------- Qidiruv ------- */
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  const buildParams = useCallback((pageNum) => {
    const params = new URLSearchParams({ page: String(pageNum), page_size: String(PAGE_SIZE), view: "sale", stock: "available" });
    if (warehouse !== "all") params.set("sklad", String(warehouse));
    if (category !== "all") params.set("kategoriya", String(category));
    if (qDebounced) params.set("search", qDebounced);
    return params;
  }, [warehouse, category, qDebounced]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`maxsulot/?${buildParams(1)}`);
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : data?.results || []);
        setPage(1);
        setHasMore(Boolean(data?.next));
      } catch {
        if (!cancelled) { setProducts([]); setHasMore(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [buildParams]);

  useEffect(() => {
    const node = loaderRef.current;
    if (!node) return;
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting || !hasMore || fetchingRef.current) return;
      fetchingRef.current = true;
      const next = page + 1;
      try {
        const { data } = await api.get(`maxsulot/?${buildParams(next)}`);
        setProducts((prev) => uniqById([...prev, ...(data?.results || [])]));
        setHasMore(Boolean(data?.next));
        setPage(next);
      } catch {
        setHasMore(false);
      } finally {
        fetchingRef.current = false;
      }
    }, { rootMargin: "600px 0px" });
    io.observe(node);
    return () => io.disconnect();
  }, [page, hasMore, buildParams]);

  /* ------- Savat ------- */
  const addToCart = useCallback((p) => {
    const stock = stockOf(p);
    if (p.can_sell === false) { toast("Bu sklad uchun savdo ruxsati yo‘q", "err"); return; }
    if (warehouse !== "all" && p.sklad && String(p.sklad) !== String(warehouse)) {
      toast("Mahsulot boshqa skladga tegishli", "err");
      return;
    }
    let blocked = false;
    setCart((old) => {
      const ex = old.find((x) => x.id === p.id);
      if (ex) {
        if (ex.qty + 1 > stock) { blocked = true; return old; }
        return old.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1, stock } : x));
      }
      if (stock <= 0) { blocked = true; return old; }
      return [...old, {
        id: p.id, name: p.nom, sku: p.seriyasi, sklad: p.sklad ?? null,
        price: priceFor(p, priceType), qty: 1, stock,
      }];
    });
    if (blocked) toast(`${p.nom}: omborda ${stock} dona`, "err");
    else setLastAddedId(p.id);
  }, [priceType, toast, warehouse]);

  // Narx turi o'zgarsa, savatdagi narxlar yangilanadi (qo'lda o'zgartirilganlari bundan mustasno)
  useEffect(() => {
    setCart((old) => old.map((it) => {
      if (it.manualPrice) return it;
      const p = products.find((pp) => pp.id === it.id);
      return p ? { ...it, price: priceFor(p, priceType) } : it;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceType]);

  const setQty = useCallback((id, qty) => {
    setCart((old) => old.map((it) => {
      if (it.id !== id) return it;
      const n = Math.max(1, Math.floor(qty) || 1);
      if (it.stock != null && n > it.stock) {
        toast(`${it.name}: omborda ${it.stock} dona`, "err");
        return { ...it, qty: it.stock };
      }
      return { ...it, qty: n };
    }));
  }, [toast]);

  const removeLine = useCallback((id) => setCart((old) => old.filter((it) => it.id !== id)), []);

  /* ------- Shtrix-kod: qidiruv maydoniga kod + Enter ------- */
  const handleSearchEnter = async () => {
    const code = q.trim();
    if (!code) return;
    const needle = code.toLowerCase();
    const local = products.filter((p) =>
      String(p.seriyasi || "").toLowerCase() === needle ||
      String(p.qr_kod || "").toLowerCase().split(/[\s,]+/).includes(needle)
    );
    // Ro'yxat sahifalangan — boshqa mosliklar uchun serverdan ham so'raymiz
    let matched = local;
    try {
      const params = new URLSearchParams({ view: "sale", code, page_size: "20" });
      if (warehouse !== "all") params.set("sklad", String(warehouse));
      const rows = asArr(await api.get(`maxsulot/?${params}`));
      matched = uniqById([...rows, ...local]);
      if (rows.length) setProducts((prev) => uniqById([...rows, ...prev]));
    } catch {}
    if (matched.length > 1) {
      // Bir kod bir nechta mahsulotda — kassir tanlaydi
      setScanChoices({ code, items: matched });
      setQ("");
      setQDebounced("");
    } else if (matched.length === 1) {
      addToCart(matched[0]);
      setQ("");
      setQDebounced("");
    } else if (products.length === 1) {
      addToCart(products[0]);
      setQ("");
    } else {
      toast(`Topilmadi: ${code}`, "err");
    }
  };

  // Hech qaysi maydon tanlanmagan bo'lsa, skaner/klaviatura matni qidiruvga tushadi
  useEffect(() => {
    const onKey = (e) => {
      if (payOpen || editLine || customerPicker || discountOpen) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const tag = String(document.activeElement?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key.length === 1 || e.key === "Enter") searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [payOpen, editLine, customerPicker, discountOpen]);

  /* ------- Hisob ------- */
  const subtotal = cart.reduce((s, it) => s + it.price * it.qty, 0);
  const disc = Math.min(Math.max(toNum(discount), 0), subtotal);
  const total = Math.max(0, Math.round(subtotal - disc));
  const itemsCount = cart.reduce((s, it) => s + it.qty, 0);
  const cartQtyById = useMemo(() => new Map(cart.map((c) => [c.id, c.qty])), [cart]);

  const cash = toNum(cashIn), card = toNum(cardIn), terminal = toNum(terminalIn);
  const pay = useMemo(() => {
    if (payMethod === "karta") return { cash: 0, card: total, terminal: 0, change: 0, ok: total > 0 };
    if (payMethod === "terminal") return { cash: 0, card: 0, terminal: total, change: 0, ok: total > 0 };
    const nonCash = payMethod === "aralash" ? card + terminal : 0;
    const cashNeed = Math.max(0, total - nonCash);
    const received = payMethod === "naqd" && !cashIn ? total : cash;
    return {
      cash: Math.min(received, cashNeed), // kassaga faqat qolgan qismi kiradi, qaytim berilgan
      card: payMethod === "aralash" ? card : 0,
      terminal: payMethod === "aralash" ? terminal : 0,
      received,
      change: Math.max(0, received - cashNeed),
      remaining: Math.max(0, cashNeed - received),
      ok: total > 0 && nonCash <= total && received >= cashNeed,
    };
  }, [payMethod, total, cash, card, terminal, cashIn]);

  const openPay = () => {
    if (!cart.length) return;
    if (!customer) { setCustomerPicker(true); return; }
    setPayMethod("naqd"); setCashIn(""); setCardIn(""); setTerminalIn(""); setMixField("cash");
    setPayOpen(true);
  };

  /* ------- Tasdiqlash ------- */
  const confirmSale = async () => {
    if (submitting || !pay.ok || !customer) return;
    setSubmitting(true);
    try {
      const clientOrderId = `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const receiptPayType = payMethod === "aralash" ? "naqd" : payMethod;
      const note = discount && disc > 0 ? `Chegirma: ${fmt(disc)} so'm` : "";
      const receiptPayload = {
        buyer_name: customer.ismi || "",
        buyer_phone: customer.telraqam1 || "",
        buyer_location: "",
        pay_type: receiptPayType,
        seller: customer.id,
        sklad: cart.find((c) => c.sklad)?.sklad ?? null,
        discount: money3(disc),
        note,
        items: cart.map((it) => ({
          product_name: it.name, qty: it.qty, price: money3(it.price), product_id: it.id, series: it.sku || null,
        })),
      };
      const income = { buyer_name: customer.ismi || "", cash_som: Math.round(pay.cash), card_som: Math.round(pay.card), terminal_som: Math.round(pay.terminal), note: "" };
      const hasIncome = income.cash_som > 0 || income.card_som > 0 || income.terminal_som > 0;

      // Chegirma qatorlar narxiga proporsional taqsimlanadi — savdo yozuvlari yakuniy summaga teng bo'lsin
      const ratio = subtotal > 0 ? (subtotal - disc) / subtotal : 1;
      const { data } = await api.post("sotilgan-maxsulot/checkout/", {
        lines: cart.map((it) => ({
          sotuvchi: customer.id,
          maxsulot: it.id,
          sklad: it.sklad,
          sotilgansoni: it.qty,
          narx: money3(it.price * ratio),
          tolov_turi: "naqd",
          nmuddat: null,
          izoh: note,
          client_order_id: clientOrderId,
          client_line_id: String(it.id),
        })),
        receipt: receiptPayload,
        kassa_income: hasIncome ? income : null,
      });

      const receipt = data?.receipt || {
        ...receiptPayload, subtotal: money3(subtotal), total: money3(total), created_at: new Date().toISOString(),
      };
      if (autoPrint) {
        printReceipt(receipt, getReceiptPrintExtras(receipt, {
          payType: receiptPayType, inCashSom: pay.cash, inCardSom: pay.card, inTerminalSom: pay.terminal, note,
        }));
      }

      const sold = new Map(cart.map((c) => [c.id, c.qty]));
      setProducts((prev) => prev.map((p) => (sold.has(p.id) ? { ...p, soni: stockOf(p) - sold.get(p.id) } : p)));
      setCart([]); setDiscount(""); setPayOpen(false);
      toast(pay.change > 0 ? `Saqlandi. Qaytim: ${fmt(pay.change)} so'm` : "Savdo saqlandi", "ok");
      focusSearch();
    } catch (e) {
      const d = e?.response?.data;
      const msg = d?.sotilgansoni?.[0] || d?.detail || d?.kassa_income || (d ? JSON.stringify(d).slice(0, 160) : e.message);
      toast(`Saqlanmadi: ${msg}`, "err");
    } finally {
      setSubmitting(false);
    }
  };

  /* ------- Xaridor tanlash ------- */
  const pickCustomer = (c, makeDefault) => {
    setCustomer(c);
    if (makeDefault) lsSet(LS_CUSTOMER, String(c.id));
    setCustomerPicker(false);
    setCustomerQ("");
    focusSearch();
  };
  const customersFiltered = useMemo(() => {
    const s = customerQ.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => `${c.ismi} ${c.telraqam1 || ""}`.toLowerCase().includes(s));
  }, [customers, customerQ]);
  const defaultCustomerId = lsGet(LS_CUSTOMER);
  const isDefaultCustomer = customer && String(customer.id) === String(defaultCustomerId);

  /* ------- Qator tahrirlash (numpad) ------- */
  const applyEdit = () => {
    if (!editLine) return;
    const v = toNum(editLine.value);
    if (editLine.field === "qty") setQty(editLine.id, v);
    else setCart((old) => old.map((it) => (it.id === editLine.id ? { ...it, price: Math.max(0, v), manualPrice: true } : it)));
    setEditLine(null);
    focusSearch();
  };

  /* ======= UI ======= */
  return (
    <div className="pos">
      {/* ===== Top bar ===== */}
      <header className="pos-top">
        <button type="button" className="pos-btn ghost" onClick={() => navigate(adminPath("Sotuv"))}>← Menyu</button>
        <div className="pos-search">
          <input
            ref={searchRef}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSearchEnter(); } }}
            placeholder="Qidirish yoki shtrix-kodni skanerlang"
            inputMode="search"
          />
          {q && <button type="button" className="pos-search-x" onClick={() => { setQ(""); focusSearch(); }}>×</button>}
        </div>
        <button type="button" className={`pos-customer${isDefaultCustomer ? "" : " custom"}`} onClick={() => setCustomerPicker(true)}>
          <span className="pos-customer-label">Xaridor</span>
          <b>{customer?.ismi || "Tanlang"}</b>
        </button>
      </header>

      {/* ===== Kategoriyalar ===== */}
      <nav className="pos-chips">
        <button type="button" className={`pos-chip${category === "all" ? " on" : ""}`} onClick={() => setCategory("all")}>Barchasi</button>
        {categories.map((c) => (
          <button key={c.id} type="button" className={`pos-chip${String(category) === String(c.id) ? " on" : ""}`} onClick={() => setCategory(c.id)}>
            {c.nom || c.name}
          </button>
        ))}
        {priceTypes.length > 1 && (
          <div className="pos-pricetype">
            {priceTypes.map((pt) => {
              const lab = String(pt.nom || "").toLowerCase();
              return (
                <button key={pt.id} type="button" className={`pos-chip small${priceType === lab ? " on" : ""}`} onClick={() => setPriceType(lab)}>
                  {pt.nom}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      <div className="pos-body">
        {/* ===== Mahsulotlar ===== */}
        <section className="pos-products">
          <div className="pos-grid">
            {products.map((p) => (
              <PosCard key={p.id} product={p} price={priceFor(p, priceType)} inCartQty={cartQtyById.get(p.id) || 0} onAdd={addToCart} />
            ))}
          </div>
          {!loading && products.length === 0 && <div className="pos-empty">Mahsulot topilmadi</div>}
          {loading && <div className="pos-empty">Yuklanmoqda…</div>}
          <div ref={loaderRef} style={{ height: 1 }} />
        </section>

        {/* ===== Savat ===== */}
        <aside className="pos-cart">
          <div className="pos-cart-head">
            <b>Savat</b>
            <span>{itemsCount} dona</span>
            {cart.length > 0 && (
              <button type="button" className="pos-btn ghost small" onClick={() => { if (window.confirm("Savatni tozalaysizmi?")) setCart([]); }}>
                Tozalash
              </button>
            )}
          </div>

          <div className="pos-lines">
            {cart.length === 0 && <div className="pos-empty">Mahsulotni bosing yoki skanerlang</div>}
            {cart.map((it) => (
              <div key={it.id} className={`pos-line${it.id === lastAddedId ? " flash" : ""}`}>
                <div className="pos-line-info">
                  <div className="pos-line-name">{it.name}</div>
                  <button type="button" className="pos-line-price" onClick={() => setEditLine({ id: it.id, field: "price", value: String(Math.round(it.price)) })}>
                    {fmt(it.price)} so'm{it.manualPrice ? " ✎" : ""}
                  </button>
                </div>
                <div className="pos-qty">
                  <button type="button" onClick={() => (it.qty <= 1 ? removeLine(it.id) : setQty(it.id, it.qty - 1))}>{it.qty <= 1 ? "🗑" : "−"}</button>
                  <button type="button" className="pos-qty-val" onClick={() => setEditLine({ id: it.id, field: "qty", value: String(it.qty) })}>{it.qty}</button>
                  <button type="button" onClick={() => setQty(it.id, it.qty + 1)}>+</button>
                </div>
                <div className="pos-line-total">{fmt(it.price * it.qty)}</div>
              </div>
            ))}
          </div>

          <div className="pos-summary">
            {disc > 0 && (
              <div className="pos-sum-row"><span>Jami</span><span>{fmt(subtotal)}</span></div>
            )}
            <button type="button" className="pos-sum-row link" onClick={() => setDiscountOpen(true)} disabled={!cart.length}>
              <span>Chegirma</span><span>{disc > 0 ? `− ${fmt(disc)}` : "qo‘shish"}</span>
            </button>
            <div className="pos-total"><span>JAMI</span><b>{fmt(total)} <small>so'm</small></b></div>
            <button type="button" className="pos-pay-btn" disabled={!cart.length} onClick={openPay}>
              TO‘LASH
            </button>
          </div>
        </aside>
      </div>

      {/* ===== To'lov oynasi ===== */}
      {payOpen && (
        <div className="pos-modal-bg" onClick={() => !submitting && setPayOpen(false)}>
          <div className="pos-modal pay" onClick={(e) => e.stopPropagation()}>
            <div className="pos-modal-head">
              <div>
                <div className="muted">To‘lanadigan summa</div>
                <div className="pos-pay-total">{fmt(total)} so'm</div>
              </div>
              <button type="button" className="pos-btn ghost" onClick={() => setPayOpen(false)} disabled={submitting}>✕</button>
            </div>

            <div className="pos-methods">
              {[["naqd", "Naqd"], ["karta", "Karta"], ["terminal", "Terminal"], ["aralash", "Aralash"]].map(([k, label]) => (
                <button key={k} type="button" className={`pos-method${payMethod === k ? " on" : ""}`} onClick={() => setPayMethod(k)}>{label}</button>
              ))}
            </div>

            {(payMethod === "naqd" || payMethod === "aralash") && (
              <div className="pos-pay-body">
                <div className="pos-pay-fields">
                  {payMethod === "aralash" && (
                    <>
                      <button type="button" className={`pos-field${mixField === "card" ? " on" : ""}`} onClick={() => setMixField("card")}>
                        <span>Karta</span><b>{fmt(cardIn)}</b>
                      </button>
                      <button type="button" className={`pos-field${mixField === "terminal" ? " on" : ""}`} onClick={() => setMixField("terminal")}>
                        <span>Terminal</span><b>{fmt(terminalIn)}</b>
                      </button>
                    </>
                  )}
                  <button type="button" className={`pos-field${payMethod === "naqd" || mixField === "cash" ? " on" : ""}`} onClick={() => setMixField("cash")}>
                    <span>Naqd olindi</span><b>{cashIn ? fmt(cashIn) : payMethod === "naqd" ? `${fmt(total)} (aniq)` : "0"}</b>
                  </button>
                  {pay.change > 0 && <div className="pos-change">Qaytim: <b>{fmt(pay.change)} so'm</b></div>}
                  {pay.remaining > 0 && <div className="pos-remaining">Yetishmaydi: <b>{fmt(pay.remaining)} so'm</b></div>}
                  {payMethod === "aralash" && card + terminal > total && <div className="pos-remaining">Karta + terminal summadan oshib ketdi</div>}

                  <div className="pos-quick">
                    {QUICK_CASH.map((v) => (
                      <button key={v} type="button" onClick={() => {
                        const set = payMethod === "aralash" && mixField === "card" ? setCardIn : payMethod === "aralash" && mixField === "terminal" ? setTerminalIn : setCashIn;
                        set((cur) => String(toNum(cur) + v));
                      }}>+{fmt(v)}</button>
                    ))}
                  </div>
                </div>
                <Numpad
                  value={payMethod === "aralash" && mixField === "card" ? cardIn : payMethod === "aralash" && mixField === "terminal" ? terminalIn : cashIn}
                  onChange={payMethod === "aralash" && mixField === "card" ? setCardIn : payMethod === "aralash" && mixField === "terminal" ? setTerminalIn : setCashIn}
                />
              </div>
            )}

            {(payMethod === "karta" || payMethod === "terminal") && (
              <div className="pos-pay-simple">
                {payMethod === "karta" ? "Karta" : "Terminal"} orqali <b>{fmt(total)} so'm</b> qabul qilinadi
              </div>
            )}

            <div className="pos-modal-foot">
              <label className="pos-check">
                <input type="checkbox" checked={autoPrint} onChange={(e) => { setAutoPrint(e.target.checked); lsSet(LS_PRINT, e.target.checked ? "1" : "0"); }} />
                Chek chiqarish
              </label>
              <button type="button" className="pos-pay-btn" disabled={!pay.ok || submitting} onClick={confirmSale}>
                {submitting ? "Saqlanmoqda…" : "TASDIQLASH"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Miqdor / narx numpad ===== */}
      {editLine && (
        <div className="pos-modal-bg" onClick={() => setEditLine(null)}>
          <div className="pos-modal small" onClick={(e) => e.stopPropagation()}>
            <div className="pos-modal-head">
              <div>
                <div className="muted">{cart.find((c) => c.id === editLine.id)?.name}</div>
                <div className="pos-edit-title">{editLine.field === "qty" ? "Miqdor" : "Narx (so'm)"}</div>
              </div>
            </div>
            <div className="pos-display">{editLine.value || "0"}</div>
            <Numpad value={editLine.value} onChange={(v) => setEditLine((e) => ({ ...e, value: v }))} />
            <div className="pos-modal-foot">
              <button type="button" className="pos-btn ghost" onClick={() => setEditLine(null)}>Bekor</button>
              <button type="button" className="pos-pay-btn" onClick={applyEdit}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Chegirma ===== */}
      {discountOpen && (
        <div className="pos-modal-bg" onClick={() => setDiscountOpen(false)}>
          <div className="pos-modal small" onClick={(e) => e.stopPropagation()}>
            <div className="pos-modal-head"><div className="pos-edit-title">Chegirma (so'm)</div></div>
            <div className="pos-display">{discount || "0"}</div>
            <div className="pos-quick">
              {[5, 10].map((pct) => (
                <button key={pct} type="button" onClick={() => setDiscount(String(Math.round((subtotal * pct) / 100)))}>{pct}%</button>
              ))}
            </div>
            <Numpad value={discount} onChange={setDiscount} />
            <div className="pos-modal-foot">
              <button type="button" className="pos-btn ghost" onClick={() => { setDiscount(""); setDiscountOpen(false); }}>Olib tashlash</button>
              <button type="button" className="pos-pay-btn" onClick={() => { setDiscountOpen(false); focusSearch(); }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Xaridor tanlash ===== */}
      {customerPicker && (
        <div className="pos-modal-bg" onClick={() => customer && setCustomerPicker(false)}>
          <div className="pos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pos-modal-head">
              <div>
                <div className="pos-edit-title">Xaridorni tanlang</div>
                {!defaultCustomerId && <div className="muted">Oddiy savdo uchun standart xaridorni belgilang — keyingi safar avtomatik tanlanadi</div>}
              </div>
              {customer && <button type="button" className="pos-btn ghost" onClick={() => setCustomerPicker(false)}>✕</button>}
            </div>
            <input className="pos-input" value={customerQ} onChange={(e) => setCustomerQ(e.target.value)} placeholder="Ism yoki telefon" />
            <div className="pos-customers">
              {customersFiltered.map((c) => {
                const debt = toNum(c.qarhaq);
                return (
                  <div key={c.id} className="pos-cust-row">
                    <button type="button" className="pos-cust-main" onClick={() => pickCustomer(c, false)}>
                      <b>{c.ismi}</b>
                      <span>{c.telraqam1 || "—"} · {c.mijoztarifi || "Donaga"}{debt < 0 ? ` · qarz ${fmt(-debt)}` : ""}</span>
                    </button>
                    <button type="button" className={`pos-btn small${String(c.id) === String(defaultCustomerId) ? " on" : " ghost"}`} onClick={() => pickCustomer(c, true)}>
                      {String(c.id) === String(defaultCustomerId) ? "Standart ✓" : "Standart qilish"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== Bir xil shtrix-kodli mahsulotlardan tanlash ===== */}
      {scanChoices && (
        <div className="pos-modal-bg" onClick={() => { setScanChoices(null); focusSearch(); }}>
          <div className="pos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pos-modal-head">
              <div>
                <div className="pos-edit-title">Qaysi mahsulot?</div>
                <div className="muted">Kod: {scanChoices.code} — {scanChoices.items.length} ta mahsulot</div>
              </div>
              <button type="button" className="pos-btn ghost" onClick={() => { setScanChoices(null); focusSearch(); }}>✕</button>
            </div>
            <div className="pos-customers">
              {scanChoices.items.map((p) => {
                const out = stockOf(p) <= 0 || p.can_sell === false;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="pos-cust-main pos-pick"
                    disabled={out}
                    onClick={() => { addToCart(p); setScanChoices(null); focusSearch(); }}
                  >
                    <b>{p.nom}</b>
                    <span>{p.seriyasi} · {out ? "Yo‘q" : `${stockOf(p)} dona`} · <b className="pos-pick-price">{fmt(priceFor(p, priceType))} so'm</b></span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== Toastlar ===== */}
      <div className="pos-toasts">
        {toasts.map((t) => <div key={t.id} className={`pos-toast ${t.kind}`}>{t.text}</div>)}
      </div>
    </div>
  );
}
